import type { Router, RouteLocationNormalized, RouteMeta } from 'vue-router';

import { Modal } from 'ant-design-vue';
import nProgress from 'nprogress';
import { unref } from 'vue';

import { useTransitionSetting } from '/@/hooks/setting/useTransitionSetting';
import { setRouteChange } from '/@/logics/mitt/routeChange';
import projectSetting from '/@/settings/projectSetting';
import { useAppStoreWithOut } from '/@/store/modules/app';
import { useMultipleTabWithOutStore } from '/@/store/modules/multipleTab';
import { useUserStoreWithOut } from '/@/store/modules/user';
import { warn } from '/@/utils/log';
import { AxiosCanceler } from '/@/utils/http/axios/axiosCancel';

import { createParamMenuGuard } from './paramMenuGuard';
import { createPermissionGuard } from './permissionGuard';
import { createStateGuard } from './stateGuard';

// Don't change the order of creation
export function setupRouterGuard(router: Router) {
  createPageGuard(router);
  createPageLoadingGuard(router);
  createHttpGuard(router);
  createScrollGuard(router);
  createMessageGuard(router);
  createProgressGuard(router);
  createPermissionGuard(router);
  createParamMenuGuard(router); // must after createPermissionGuard (menu has been built.)
  createStateGuard(router);
}

/**
 * Hooks for handling page state.
 */
function createPageGuard(router: Router) {
  const loadedPageMap = new Map<string, boolean>();
  const userStore = useUserStoreWithOut();
  const tabStore = useMultipleTabWithOutStore();

  router.beforeEach(async (to) => {
    // Skip extra loading work for routes that have already been visited.
    to.meta.loaded = !!loadedPageMap.get(to.path);
    // Notify route changes early so subscribers can sync state.
    setRouteChange(to);

    return true;
  });

  router.afterEach(async (to, from) => {
    loadedPageMap.set(to.path, true);

    const { name, path, fullPath, meta = {} } = to;
    if (!name || !userStore.getToken) {
      return;
    }

    const sourceRoute = from?.fullPath ? from : router.currentRoute.value || ({ fullPath, path } as RouteLocationNormalized);
    const { currentActiveMenu, hideTab } = meta as RouteMeta;
    const isHide = !hideTab ? null : currentActiveMenu;

    if (isHide) {
      // resolve 可获得完整 RouteLocationNormalized（含 fullPath/query/params/matched 等），
      // 避免使用 router.getRoutes().find() 返回的 RouteRecord 做不安全断言导致幽灵 tab。
      try {
        const parentResolved = router.resolve(currentActiveMenu as string);
        if (parentResolved && parentResolved.name) {
          await tabStore.addTab(parentResolved as unknown as RouteLocationNormalized, sourceRoute);
        }
      } catch (e) {
        warn(`[routerGuard] resolve currentActiveMenu failed: ${String(currentActiveMenu)}`);
      }
      return;
    }

    await tabStore.addTab(to, sourceRoute);
  });
}

// Used to handle page loading status.
function createPageLoadingGuard(router: Router) {
  const userStore = useUserStoreWithOut();
  const appStore = useAppStoreWithOut();
  const { getOpenPageLoading } = useTransitionSetting();

  router.beforeEach(async (to) => {
    if (!userStore.getToken) {
      return true;
    }
    if (to.meta.loaded) {
      return true;
    }

    if (unref(getOpenPageLoading)) {
      appStore.setPageLoadingAction(true);
      return true;
    }

    return true;
  });

  router.afterEach(async () => {
    if (unref(getOpenPageLoading)) {
      // The timer simulates loading time to avoid flashing too fast.
      setTimeout(() => {
        appStore.setPageLoading(false);
      }, 220);
    }
    return true;
  });
}

/**
 * Close pending requests when the route switches.
 */
function createHttpGuard(router: Router) {
  const { removeAllHttpPending } = projectSetting;
  let axiosCanceler: Nullable<AxiosCanceler>;
  if (removeAllHttpPending) {
    axiosCanceler = new AxiosCanceler();
  }

  router.beforeEach(async () => {
    axiosCanceler?.removeAllPending();
    return true;
  });
}

// Scroll back to the top on hash routes.
function createScrollGuard(router: Router) {
  const isHash = (href: string) => {
    return /^#/.test(href);
  };

  const body = document.body;

  router.afterEach(async (to) => {
    isHash((to as RouteLocationNormalized & { href: string })?.href) && body.scrollTo(0, 0);
    return true;
  });
}

/**
 * Close message instances when the route switches.
 */
export function createMessageGuard(router: Router) {
  const { closeMessageOnSwitch } = projectSetting;

  router.beforeEach(async () => {
    try {
      if (closeMessageOnSwitch) {
        Modal.destroyAll();
        // Keep notification instances visible during route switches.
        // notification.destroy();
      }
    } catch (error) {
      warn('message guard error:' + error);
    }
    return true;
  });
}

export function createProgressGuard(router: Router) {
  const { getOpenNProgress } = useTransitionSetting();

  router.beforeEach(async (to) => {
    if (to.meta.loaded) {
      return true;
    }
    unref(getOpenNProgress) && nProgress.start();
    return true;
  });

  router.afterEach(async () => {
    unref(getOpenNProgress) && nProgress.done();
    return true;
  });
}
